// Comments on a Drive file — thread list, composer with @mentions, and the
// editors' header button + right-side panel. Identical file in Drive, Docs,
// Sheets, Slides and PDF (paths in commentsApi.ts) — change every copy
// together. Each app passes a small CommentsAdapter that turns its current
// selection into an anchor and jumps back to one. Comment bodies are always
// rendered as text, never as HTML.
//
// This (Docs) copy adds, additively: `docked` on CommentsPanel (an in-layout
// pane on lg+) and the adapter's optional highlight / clearHighlight /
// subscribeHighlightCleared (the active thread keeps its text marked). Apps
// that don't pass them behave exactly as before; other copies can take it as-is.

import {
    useCallback, useEffect, useId, useMemo, useRef, useState,
    type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type MutableRefObject, type RefObject
} from "react";
import { useAuth } from "react-oidc-context";
import { Check, Loader2, MapPin, MessageSquare, Pencil, RotateCcw, Send, Trash2, UserPlus, X } from "@fg/icons";
import {
    MentionAccessError, commentErrorText, commentsApi, formatCommentTime, nameFromEmail, splitMentions,
    type CommentList, type CommentPerson, type CommentThread, type DriveComment
} from "./commentsApi";

// ── Public API ───────────────────────────────────────────────────────────

/** Where a new comment points, as the editor describes its selection. */
export interface CommentAnchor {
    /** "p:0;12;0", "Sheet1!B4:C6", "slide:3;id=…", "page:2" — at most 128 characters. */
    anchor: string | null;
    /** The selected text / value (clipped to 500 characters). */
    quote: string | null;
}

/** What an app plugs in so comments follow its selection. All optional (Drive has none). */
export interface CommentsAdapter {
    /** The current selection as an anchor, or null when nothing useful is selected. */
    captureAnchor?: () => CommentAnchor | null;
    /** Readable label for a stored anchor ("Paragraph 3", "Slide 2", "Page 4"); null = show it raw. */
    describeAnchor?: (anchor: string) => string | null;
    /** Jump to where a comment points. False = the spot couldn't be found. */
    navigate?: (anchor: string | null, quote: string | null) => boolean;
    /**
     * Like `navigate`, and keeps the spot marked while the thread is active
     * (Docs: the editor's search highlight — never a change to the file).
     * Used instead of `navigate` when present.
     */
    highlight?: (anchor: string | null, quote: string | null) => boolean;
    /** Drops the mark `highlight` made (no-op when there is none). */
    clearHighlight?: () => void;
    /** The app dropped the mark itself (e.g. the caret moved away). Returns an unsubscribe. */
    subscribeHighlightCleared?: (listener: () => void) => () => void;
    /** Shown in the composer while no selection is attached. */
    selectionHint?: string;
}

/** "onPrimary" sits on the suite top bar (`.suite-appbar-*` classes); "surface" on a plain toolbar. */
export type CommentsTone = "onPrimary" | "surface";

export const COMMENTS_SHORTCUT = "Ctrl+Alt+M";

/** Ctrl+Alt+M (⌘ on a Mac) — Google Docs' "insert comment". */
export function isCommentsShortcut(e: KeyboardEvent): boolean {
    return (e.ctrlKey || e.metaKey) && e.altKey && !e.shiftKey && (e.code === "KeyM" || e.key.toLowerCase() === "m");
}

/** How often an open panel / tab checks for other people's comments. */
export const COMMENTS_POLL_MS = 30_000;

// ── Shared bits ──────────────────────────────────────────────────────────

function cx(...parts: Array<string | false | null | undefined>): string {
    return parts.filter(Boolean).join(" ");
}

function useToken(): { tokenRef: MutableRefObject<string | undefined>; hasToken: boolean } {
    const auth = useAuth();
    const token = auth.user?.access_token;
    const tokenRef = useRef(token);
    tokenRef.current = token;
    return { tokenRef, hasToken: Boolean(token) };
}

const SMALL_BTN =
    "inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
const PRIMARY_BTN =
    "inline-flex h-7 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50";
const ICON_BTN =
    "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

const OUTLINE_BTN =
    "inline-flex h-[26px] items-center gap-1 rounded border border-input bg-background px-2.5 text-xs font-semibold text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";

const AVATAR_TONES = [
    "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300",
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
    "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
    "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300",
    "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-950/60 dark:text-cyan-300"
];

function Avatar({ email, name, small }: { email: string; name: string; small?: boolean }) {
    let h = 0;
    const key = email.toLowerCase();
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    const words = (name || email).split(/[\s._@-]+/).filter(Boolean);
    const initials = ((words[0]?.[0] ?? "?") + (words.length > 1 ? words[1]?.[0] ?? "" : "")).toUpperCase();
    return (
        <span
            aria-hidden
            className={cx(
                "flex shrink-0 items-center justify-center rounded-full font-semibold",
                small ? "h-6 w-6 text-[10px]" : "h-[26px] w-[26px] text-[10px]",
                AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length]
            )}
        >
            {initials}
        </span>
    );
}

/** A body as text runs with @mentions highlighted — text nodes only, never HTML. */
function MentionText({ body }: { body: string }) {
    return (
        <>
            {splitMentions(body).map((part, i) =>
                part.mention ? (
                    <span
                        key={i}
                        title={part.mention}
                        className="rounded bg-primary/10 px-0.5 font-medium text-primary dark:bg-primary/20"
                    >
                        {part.text}
                    </span>
                ) : (
                    <span key={i}>{part.text}</span>
                )
            )}
        </>
    );
}

// ── Header button + open-count ───────────────────────────────────────────

/**
 * Open-thread count for the header badge: fetched when the file changes and
 * when the window regains focus; an open panel keeps it current via `set`.
 */
export function useOpenCommentCount(fileId: string | null): { count: number; set: (n: number) => void; refresh: () => void } {
    const { tokenRef, hasToken } = useToken();
    const [count, setCount] = useState(0);
    const [nonce, setNonce] = useState(0);

    useEffect(() => { setCount(0); }, [fileId]);
    useEffect(() => {
        if (!fileId || !hasToken) return;
        let cancelled = false;
        commentsApi.count(fileId, tokenRef.current).then(
            (c) => { if (!cancelled) setCount(c.open); },
            () => { /* the badge is a hint — never an error */ }
        );
        return () => { cancelled = true; };
    }, [fileId, hasToken, nonce, tokenRef]);
    useEffect(() => {
        const onFocus = () => setNonce((n) => n + 1);
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, []);

    const refresh = useCallback(() => setNonce((n) => n + 1), []);
    return { count: fileId ? count : 0, set: setCount, refresh };
}

/** Speech-bubble button next to Version history, with the open-thread count. */
export function CommentsButton({
    file, openCount, active, onClick, tone
}: {
    /** Null until the file is in Drive — the button is inert then. */
    file: { id: string } | null;
    openCount: number;
    /** The panel is open. */
    active: boolean;
    onClick: () => void;
    tone: CommentsTone;
}) {
    const disabled = file === null;
    const label = openCount > 0 ? `Comments, ${openCount} open` : "Comments";
    return (
        <button
            type="button"
            onClick={() => { if (!disabled) onClick(); }}
            aria-label={label}
            aria-haspopup="dialog"
            aria-expanded={disabled ? undefined : active}
            aria-disabled={disabled || undefined}
            aria-keyshortcuts="Control+Alt+M"
            title={disabled ? "Save to Drive first" : `${label} (${COMMENTS_SHORTCUT})`}
            className={cx(
                "relative inline-flex h-7 w-7 shrink-0 items-center justify-center rounded",
                // On the suite top bar, `.suite-appbar-btn` handles hover, focus and
                // the open state (aria-expanded) for every bar style.
                tone === "onPrimary"
                    ? "suite-appbar-btn"
                    : "text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                disabled
                    ? cx("cursor-not-allowed opacity-50", tone === "onPrimary" && "hover:bg-transparent")
                    : tone === "onPrimary" ? undefined : "hover:bg-accent hover:text-foreground",
                active && !disabled && tone !== "onPrimary" && "bg-accent text-foreground"
            )}
        >
            <MessageSquare className="h-4 w-4" aria-hidden />
            {openCount > 0 && !disabled ? (
                <span
                    aria-hidden
                    className={cx(
                        "absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none",
                        tone === "onPrimary" ? "bg-[var(--appbar-fg)] text-[var(--appbar-bg)]" : "bg-primary text-primary-foreground"
                    )}
                >
                    {openCount > 99 ? "99+" : openCount}
                </span>
            ) : null}
        </button>
    );
}

// ── Editor panel ─────────────────────────────────────────────────────────

export interface CommentsPanelProps {
    file: { id: string; name: string };
    onClose: () => void;
    adapter?: CommentsAdapter;
    /** Keeps the header badge in step while the panel polls. */
    onOpenCountChange?: (open: number) => void;
    /**
     * On wide screens (lg+) sit in the editor's layout — a bordered pane beside
     * the file rather than an overlay covering it. Narrower, it stays an overlay.
     */
    docked?: boolean;
}

/** The editors' right-side Comments panel (same footprint as Version history, or docked). */
export function CommentsPanel({ file, onClose, adapter, onOpenCountChange, docked }: CommentsPanelProps) {
    const titleId = useId();
    const closeRef = useRef<HTMLButtonElement>(null);
    const [openCount, setOpenCount] = useState<number | null>(null);
    const countChangeRef = useRef(onOpenCountChange);
    countChangeRef.current = onOpenCountChange;
    const onCount = useCallback((open: number) => {
        setOpenCount(open);
        countChangeRef.current?.(open);
    }, []);

    // Focus the panel on open; hand focus back to whatever opened it.
    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        closeRef.current?.focus();
        return () => {
            if (previous && document.contains(previous)) previous.focus();
        };
    }, []);

    const closeButton = (
        <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close comments"
            title="Close (Esc)"
            className={cx(
                "inline-flex shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                docked ? "h-7 w-7" : "h-8 w-8"
            )}
        >
            <X className="h-4 w-4" aria-hidden />
        </button>
    );

    return (
        <aside
            role="dialog"
            aria-modal="false"
            aria-labelledby={titleId}
            onKeyDown={(e) => {
                if (e.key === "Escape" && !e.defaultPrevented) {
                    e.stopPropagation();
                    onClose();
                }
            }}
            className={cx(
                "fixed inset-y-0 right-0 z-[65] flex w-full max-w-sm flex-col border-l bg-background text-foreground shadow-2xl",
                docked && "lg:static lg:z-auto lg:w-80 lg:max-w-none lg:shrink-0 lg:bg-[var(--docs-pane)] lg:shadow-none"
            )}
        >
            {docked ? (
                <header className="flex h-10 shrink-0 items-center gap-1.5 pl-3.5 pr-2">
                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                    <h2 id={titleId} className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Comments{openCount ? ` · ${openCount}` : ""}
                    </h2>
                    {closeButton}
                </header>
            ) : (
                <header className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
                    <MessageSquare className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    <div className="min-w-0 flex-1">
                        <h2 id={titleId} className="truncate text-sm font-semibold">Comments</h2>
                        <p className="truncate text-xs text-muted-foreground" title={file.name}>{file.name}</p>
                    </div>
                    {closeButton}
                </header>
            )}
            <CommentsView
                fileId={file.id}
                adapter={adapter}
                active
                autoFocus
                onOpenCountChange={onCount}
                // On a phone the panel covers the file — get out of the way.
                onNavigated={() => { if (window.matchMedia("(max-width: 639px)").matches) onClose(); }}
            />
        </aside>
    );
}

// ── Thread list + composer ───────────────────────────────────────────────

export interface CommentsViewProps {
    fileId: string;
    adapter?: CommentsAdapter;
    /** Poll for other people's comments every 30 s while true. */
    active: boolean;
    /** Focus the composer once the list has loaded (again whenever focusKey changes). */
    autoFocus?: boolean;
    focusKey?: number;
    onOpenCountChange?: (open: number) => void;
    /** A comment was posted, edited, deleted, resolved or reopened. */
    onChanged?: () => void;
    /** A thread's anchor was found and shown. */
    onNavigated?: () => void;
}

interface PendingMention {
    missing: string[];
    message: string;
    send: (ignoreMissingAccess: boolean) => Promise<unknown>;
    resolve: (posted: boolean) => void;
}

interface ThreadActions {
    reply: (rootId: string, text: string) => Promise<boolean>;
    edit: (commentId: string, text: string) => Promise<boolean>;
    remove: (commentId: string) => Promise<void>;
    setResolved: (rootId: string, resolved: boolean) => Promise<void>;
    navigated: (found: boolean) => void;
}

/** Threads + composer — the body of the editors' panel and of Drive's "Comments" tab. */
export function CommentsView(props: CommentsViewProps) {
    const { fileId, adapter, active } = props;
    const auth = useAuth();
    const me = (auth.user?.profile.email ?? "").toLowerCase();
    const { tokenRef, hasToken } = useToken();
    const latest = useRef(props);
    latest.current = props;

    const [list, setList] = useState<CommentList | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [showResolved, setShowResolved] = useState(false);
    const [announcement, setAnnouncement] = useState("");
    const [people, setPeople] = useState<CommentPerson[] | null>(null);
    const [pending, setPending] = useState<PendingMention | null>(null);
    const [pendingBusy, setPendingBusy] = useState(false);
    const [pendingError, setPendingError] = useState<string | null>(null);

    const seenRef = useRef<Set<string> | null>(null);
    const peopleLoadingRef = useRef(false);
    const composerRef = useRef<HTMLTextAreaElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const pendingRef = useRef<HTMLDivElement>(null);
    const focusedForRef = useRef<number | null>(null);

    // Re-setting the same text wouldn't be re-announced; a trailing NBSP makes it new.
    const say = useCallback((text: string) => {
        setAnnouncement((prev) => (prev === text ? `${text} ` : text));
    }, []);

    // The thread whose spot is marked in the file (adapter.highlight).
    const [activeId, setActiveId] = useState<string | null>(null);

    // Another file — start clean.
    useEffect(() => {
        setList(null);
        setLoadError(null);
        setPeople(null);
        setPending(null);
        setActiveId(null);
        seenRef.current = null;
    }, [fileId]);

    // The app dropped the mark itself — the caret moved elsewhere in the file.
    useEffect(() => adapter?.subscribeHighlightCleared?.(() => setActiveId(null)), [adapter]);
    // Closing the panel (or switching file) takes the mark with it.
    useEffect(() => () => adapter?.clearHighlight?.(), [adapter, fileId]);
    // A thread that left the list (resolved, deleted) can't stay marked.
    useEffect(() => {
        if (!activeId || !list || list.threads.some((t) => t.root.id === activeId)) return;
        adapter?.clearHighlight?.();
        setActiveId(null);
    }, [activeId, adapter, list]);

    const load = useCallback(async (opts: { announceNew?: boolean; quiet?: boolean } = {}) => {
        if (!tokenRef.current) return;
        try {
            const next = await commentsApi.list(fileId, showResolved, tokenRef.current);
            const seen = seenRef.current;
            const all = new Set(seen ?? []);
            const fresh: DriveComment[] = [];
            for (const t of next.threads) {
                for (const c of [t.root, ...t.replies]) {
                    all.add(c.id);
                    if (seen && !seen.has(c.id) && !c.isDeleted && c.authorEmail !== me) fresh.push(c);
                }
            }
            seenRef.current = all;
            if (opts.announceNew && fresh.length > 0) {
                const first = fresh[0]!;
                say(fresh.length === 1 ? `New comment from ${first.authorName}: ${first.body.slice(0, 120)}` : `${fresh.length} new comments`);
            }
            setList(next);
            setLoadError(null);
            latest.current.onOpenCountChange?.(next.openCount);
        } catch (err) {
            if (!opts.quiet) setLoadError(commentErrorText(err));
        }
    }, [fileId, showResolved, me, say, tokenRef]);

    useEffect(() => { void load(); }, [load, hasToken]);

    // Poll while showing (no realtime channel here) — only when the tab is visible.
    useEffect(() => {
        if (!active) return;
        const tick = () => {
            if (document.visibilityState === "visible") void load({ announceNew: true, quiet: true });
        };
        const timer = window.setInterval(tick, COMMENTS_POLL_MS);
        document.addEventListener("visibilitychange", tick);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", tick);
        };
    }, [active, load]);

    // Focus the composer once there is one.
    useEffect(() => {
        if (!props.autoFocus || !list) return;
        const key = props.focusKey ?? 0;
        if (focusedForRef.current === key) return;
        focusedForRef.current = key;
        composerRef.current?.focus();
    }, [list, props.autoFocus, props.focusKey]);

    const needPeople = useCallback(() => {
        if (people || peopleLoadingRef.current) return;
        peopleLoadingRef.current = true;
        commentsApi.people(fileId, tokenRef.current).then(
            (p) => setPeople(p.people),
            () => setPeople([])
        ).finally(() => { peopleLoadingRef.current = false; });
    }, [fileId, people, tokenRef]);

    const afterWrite = useCallback(async (message: string) => {
        say(message);
        await load();
        latest.current.onChanged?.();
    }, [load, say]);

    /**
     * Sends a write that may mention people. A 409 (mentioned people can't
     * open the file) becomes the "share with them?" prompt; resolves true
     * once the write went through, false when the user backed out.
     */
    const withMentions = useCallback(async (send: (ignoreMissingAccess: boolean) => Promise<unknown>): Promise<boolean> => {
        try {
            await send(false);
            return true;
        } catch (err) {
            if (!(err instanceof MentionAccessError)) throw err;
            setPendingError(null);
            return new Promise<boolean>((resolve) => {
                setPending({ missing: err.missing, message: err.message, send, resolve });
            });
        }
    }, []);

    useEffect(() => {
        if (!pending) return;
        pendingRef.current?.scrollIntoView({ block: "nearest" });
        pendingRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    }, [pending]);

    const settlePending = async (mode: "share" | "anyway" | "cancel") => {
        const p = pending;
        if (!p) return;
        if (mode === "cancel") {
            setPending(null);
            p.resolve(false);
            return;
        }
        setPendingBusy(true);
        setPendingError(null);
        try {
            if (mode === "share") await commentsApi.shareAsViewers(fileId, p.missing, tokenRef.current);
            await p.send(true);
            setPending(null);
            if (mode === "share") {
                setPeople(null);
                say(`Shared the file with ${p.missing.join(", ")}`);
            }
            p.resolve(true);
        } catch (err) {
            setPendingError(commentErrorText(err));
        } finally {
            setPendingBusy(false);
        }
    };

    const postNew = async (text: string, anchor: CommentAnchor | null): Promise<boolean> => {
        const ok = await withMentions((ignore) => commentsApi.create(
            fileId,
            { body: text, anchor: anchor?.anchor ?? null, quote: anchor?.quote ?? null, ignoreMissingAccess: ignore },
            tokenRef.current
        ));
        if (ok) await afterWrite("Comment posted");
        return ok;
    };

    const actions: ThreadActions = {
        reply: async (rootId, text) => {
            const ok = await withMentions((ignore) => commentsApi.reply(rootId, { body: text, ignoreMissingAccess: ignore }, tokenRef.current));
            if (ok) await afterWrite("Reply posted");
            return ok;
        },
        edit: async (commentId, text) => {
            const ok = await withMentions((ignore) => commentsApi.edit(commentId, { body: text, ignoreMissingAccess: ignore }, tokenRef.current));
            if (ok) await afterWrite("Comment updated");
            return ok;
        },
        remove: async (commentId) => {
            await commentsApi.remove(commentId, tokenRef.current);
            listRef.current?.focus();
            await afterWrite("Comment deleted");
        },
        setResolved: async (rootId, resolved) => {
            await (resolved ? commentsApi.resolve(rootId, tokenRef.current) : commentsApi.reopen(rootId, tokenRef.current));
            // A resolved thread leaves the list unless resolved ones are shown.
            if (resolved && !showResolved) listRef.current?.focus();
            await afterWrite(resolved ? "Thread resolved" : "Thread reopened");
        },
        navigated: (found) => {
            if (found) latest.current.onNavigated?.();
            else say("Couldn't find that spot in the file — it may have changed since the comment was made.");
        }
    };

    if (list === null) {
        return (
            <div className="flex min-h-0 flex-1 flex-col">
                <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
                {loadError ? (
                    <div className="flex flex-col items-start gap-2 p-4 text-sm">
                        <p role="alert" className="text-destructive">Couldn't load comments. {loadError}</p>
                        <button type="button" onClick={() => { setLoadError(null); void load(); }} className={SMALL_BTN}>
                            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Try again
                        </button>
                    </div>
                ) : (
                    <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground" aria-busy="true">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading comments…
                    </p>
                )}
            </div>
        );
    }

    const threads = list.threads;
    const canShare = list.myRole !== "viewer";

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <p className="sr-only" role="status" aria-live="polite">{announcement}</p>

            <div className="shrink-0 border-b p-3">
                {list.canComment ? (
                    <NewCommentForm
                        key={fileId}
                        adapter={adapter}
                        people={people}
                        onNeedPeople={needPeople}
                        textareaRef={composerRef}
                        onSubmit={postNew}
                    />
                ) : (
                    <p className="text-xs text-muted-foreground">
                        Comments can be added to files that aren't in the bin.
                    </p>
                )}
            </div>

            {pending ? (
                <div
                    ref={pendingRef}
                    role="group"
                    aria-label="Mentioned people without access"
                    className="shrink-0 space-y-2 border-b bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                >
                    <p className="font-medium">{pending.message}</p>
                    <p className="break-words">
                        {canShare
                            ? `Share the file with ${pending.missing.join(", ")} as ${pending.missing.length === 1 ? "a viewer" : "viewers"} so they can see it?`
                            : `${pending.missing.join(", ")} can't open this file. Ask its owner to share it with them.`}
                    </p>
                    {pendingError ? <p role="alert" className="text-destructive">{pendingError}</p> : null}
                    <div className="flex flex-wrap gap-1.5">
                        {canShare ? (
                            <button type="button" onClick={() => void settlePending("share")} disabled={pendingBusy} className={PRIMARY_BTN}>
                                {pendingBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <UserPlus className="h-3.5 w-3.5" aria-hidden />}
                                Share and post
                            </button>
                        ) : null}
                        <button type="button" onClick={() => void settlePending("anyway")} disabled={pendingBusy} className={SMALL_BTN}>
                            Post without sharing
                        </button>
                        <button type="button" onClick={() => void settlePending("cancel")} disabled={pendingBusy} className={SMALL_BTN}>
                            Cancel
                        </button>
                    </div>
                </div>
            ) : null}

            <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2 text-xs text-muted-foreground">
                <span>
                    {list.openCount === 0 ? "No open comments" : `${list.openCount} open`}
                    {list.resolvedCount > 0 ? ` · ${list.resolvedCount} resolved` : ""}
                </span>
                <label className="flex cursor-pointer items-center gap-1.5">
                    <input
                        type="checkbox"
                        checked={showResolved}
                        onChange={(e) => setShowResolved(e.target.checked)}
                        className="h-3.5 w-3.5 accent-primary"
                    />
                    Show resolved
                </label>
            </div>

            <div ref={listRef} tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto px-3 pb-4 focus:outline-none">
                {threads.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-sm text-muted-foreground">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                            <MessageSquare className="h-5 w-5" aria-hidden />
                        </span>
                        {list.resolvedCount > 0 && !showResolved
                            ? "Every comment is resolved."
                            : "No comments yet."}
                    </div>
                ) : (
                    <ul className="space-y-3" aria-label="Comment threads">
                        {threads.map((t) => (
                            <li key={t.root.id}>
                                <ThreadCard
                                    thread={t}
                                    active={t.root.id === activeId}
                                    onActivate={() => setActiveId(t.root.id)}
                                    adapter={adapter}
                                    canComment={list.canComment}
                                    people={people}
                                    onNeedPeople={needPeople}
                                    actions={actions}
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

// ── Composer ─────────────────────────────────────────────────────────────

function NewCommentForm({
    adapter, people, onNeedPeople, textareaRef, onSubmit
}: {
    adapter?: CommentsAdapter;
    people: CommentPerson[] | null;
    onNeedPeople: () => void;
    textareaRef: RefObject<HTMLTextAreaElement>;
    onSubmit: (text: string, anchor: CommentAnchor | null) => Promise<boolean>;
}) {
    const [draft, setDraft] = useState("");
    const [anchor, setAnchor] = useState<CommentAnchor | null>(null);
    const [note, setNote] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const capture = adapter?.captureAnchor;

    // Whatever was selected when the panel opened.
    useEffect(() => {
        if (!capture) return;
        try { setAnchor(capture()); } catch { /* the editor isn't ready */ }
    }, [capture]);

    const useSelection = () => {
        if (!capture) return;
        let next: CommentAnchor | null = null;
        try { next = capture(); } catch { next = null; }
        if (next) {
            setAnchor(next);
            setNote(null);
        } else {
            setNote("Nothing is selected — the comment will be about the whole file.");
        }
    };

    const submit = async () => {
        const text = draft.trim();
        if (!text || busy) return;
        setBusy(true);
        setError(null);
        try {
            if (await onSubmit(text, anchor)) {
                setDraft("");
                setAnchor(null);
                setNote(null);
            }
        } catch (err) {
            setError(commentErrorText(err));
        } finally {
            setBusy(false);
        }
    };

    const label = anchor?.anchor ? adapter?.describeAnchor?.(anchor.anchor) ?? anchor.anchor : null;

    return (
        <form
            aria-label="New comment"
            className="space-y-2"
            onSubmit={(e) => {
                e.preventDefault();
                void submit();
            }}
        >
            {anchor && (label || anchor.quote) ? (
                <div className="flex items-start gap-1.5 rounded-md border-l-2 border-primary bg-primary/5 py-1 pl-2 pr-1 text-xs dark:bg-primary/10">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                    <span className="min-w-0 flex-1">
                        <span className="sr-only">Commenting on </span>
                        {label ? <span className="font-medium">{label}</span> : null}
                        {anchor.quote ? (
                            <span className="line-clamp-2 break-words italic text-muted-foreground">“{anchor.quote}”</span>
                        ) : null}
                    </span>
                    <button
                        type="button"
                        onClick={() => setAnchor(null)}
                        aria-label="Don't attach the selection"
                        title="Comment on the whole file instead"
                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                </div>
            ) : capture ? (
                <p className="text-xs text-muted-foreground">
                    {note ?? adapter?.selectionHint ?? "Select something in the file to comment on it."}
                </p>
            ) : null}

            <MentionTextarea
                value={draft}
                onChange={setDraft}
                onSubmit={() => void submit()}
                people={people}
                onNeedPeople={onNeedPeople}
                label="Comment"
                placeholder="Add a comment — type @ to mention someone"
                textareaRef={textareaRef}
                disabled={busy}
                onFocus={() => {
                    // Pick up a selection made since the panel opened, but never
                    // drop one already attached (focusing can clear some selections).
                    if (!capture || draft) return;
                    try {
                        const next = capture();
                        if (next) setAnchor(next);
                    } catch { /* keep what we have */ }
                }}
            />
            {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
            <div className="flex items-center gap-1.5">
                {capture ? (
                    <button type="button" onClick={useSelection} className={SMALL_BTN} title="Attach what's selected in the file now">
                        <MapPin className="h-3.5 w-3.5" aria-hidden /> Use selection
                    </button>
                ) : null}
                <span className="flex-1" />
                {draft.length > 3500 ? (
                    <span className="text-[11px] text-muted-foreground" aria-live="polite">{draft.length}/4000</span>
                ) : null}
                {draft ? (
                    <button type="button" onClick={() => { setDraft(""); setError(null); }} disabled={busy} className={SMALL_BTN}>
                        Cancel
                    </button>
                ) : null}
                <button type="submit" disabled={!draft.trim() || busy} className={PRIMARY_BTN} title="Comment (Ctrl+Enter)">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Send className="h-3.5 w-3.5" aria-hidden />}
                    Comment
                </button>
            </div>
        </form>
    );
}

const MENTION_QUERY_RE = /(?:^|\s)@([^\s]*)$/;

/** A textarea with @ autocomplete over the people who have access (ARIA combobox). */
function MentionTextarea({
    value, onChange, onSubmit, onEscape, onFocus, people, onNeedPeople, label, placeholder, textareaRef, disabled, rows = 2
}: {
    value: string;
    onChange: (value: string) => void;
    /** Ctrl/⌘+Enter. */
    onSubmit: () => void;
    /** Esc with no suggestion list open (e.g. cancel an edit). */
    onEscape?: () => void;
    onFocus?: () => void;
    people: CommentPerson[] | null;
    onNeedPeople: () => void;
    label: string;
    placeholder?: string;
    textareaRef?: RefObject<HTMLTextAreaElement>;
    disabled?: boolean;
    rows?: number;
}) {
    const listId = useId();
    const ownRef = useRef<HTMLTextAreaElement>(null);
    const ref = textareaRef ?? ownRef;
    const [query, setQuery] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const matches = useMemo(() => {
        if (query === null || !people) return [];
        const q = query.toLowerCase();
        return people
            .filter((p) => p.email.includes(q) || nameFromEmail(p.email).toLowerCase().includes(q))
            .slice(0, 6);
    }, [query, people]);
    const open = query !== null && matches.length > 0;
    const activeMatch = open ? Math.min(activeIndex, matches.length - 1) : 0;

    const sync = (el: HTMLTextAreaElement) => {
        const upto = el.value.slice(0, el.selectionStart ?? el.value.length);
        const m = MENTION_QUERY_RE.exec(upto);
        const next = m ? m[1] ?? "" : null;
        if (next !== null) onNeedPeople();
        if (next !== query) setActiveIndex(0);
        setQuery(next);
    };

    const pick = (p: CommentPerson) => {
        const el = ref.current;
        if (!el) return;
        const caret = el.selectionStart ?? value.length;
        const m = MENTION_QUERY_RE.exec(value.slice(0, caret));
        if (!m) return;
        const start = caret - (m[1]?.length ?? 0) - 1; // the "@"
        const next = `${value.slice(0, start)}@${p.email} ${value.slice(caret)}`;
        const pos = start + p.email.length + 2;
        onChange(next);
        setQuery(null);
        window.requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(pos, pos);
        });
    };

    const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
        if (open) {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                const step = e.key === "ArrowDown" ? 1 : -1;
                setActiveIndex((activeMatch + step + matches.length) % matches.length);
                return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
                const p = matches[activeMatch];
                if (p) {
                    e.preventDefault();
                    pick(p);
                    return;
                }
            }
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setQuery(null);
                return;
            }
        }
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            onSubmit();
        } else if (e.key === "Escape" && onEscape) {
            e.preventDefault();
            e.stopPropagation();
            onEscape();
        }
    };

    return (
        <div className="relative">
            <textarea
                ref={ref}
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    sync(e.target);
                }}
                onSelect={(e) => sync(e.currentTarget)}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                onBlur={() => setQuery(null)}
                role="combobox"
                aria-label={label}
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls={listId}
                aria-activedescendant={open ? `${listId}-${activeMatch}` : undefined}
                placeholder={placeholder}
                rows={rows}
                maxLength={4000}
                disabled={disabled}
                className="block min-h-[2.5rem] w-full resize-y rounded-md border border-input bg-background px-2.5 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            />
            <ul
                id={listId}
                role="listbox"
                aria-label="People to mention"
                hidden={!open}
                className="absolute inset-x-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-lg"
            >
                {open
                    ? matches.map((p, i) => (
                        <li
                            key={p.email}
                            id={`${listId}-${i}`}
                            role="option"
                            aria-selected={i === activeMatch}
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setActiveIndex(i)}
                            onClick={() => pick(p)}
                            className={cx(
                                "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm",
                                i === activeMatch && "bg-accent text-accent-foreground"
                            )}
                        >
                            <Avatar email={p.email} name={nameFromEmail(p.email)} small />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate">{nameFromEmail(p.email)}</span>
                                <span className="block truncate text-xs text-muted-foreground">{p.email}</span>
                            </span>
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{p.role}</span>
                        </li>
                    ))
                    : null}
            </ul>
        </div>
    );
}

// ── Threads ──────────────────────────────────────────────────────────────

function ThreadCard({
    thread, adapter, canComment, people, onNeedPeople, actions, active, onActivate
}: {
    thread: CommentThread;
    adapter?: CommentsAdapter;
    canComment: boolean;
    people: CommentPerson[] | null;
    onNeedPeople: () => void;
    actions: ThreadActions;
    /** Its spot is marked in the file (amber border). */
    active: boolean;
    onActivate: () => void;
}) {
    const { root, replies } = thread;
    const [replyOpen, setReplyOpen] = useState(false);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState<"reply" | "resolve" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const replyRef = useRef<HTMLTextAreaElement>(null);
    const replyButtonRef = useRef<HTMLButtonElement>(null);

    const label = root.anchor ? adapter?.describeAnchor?.(root.anchor) ?? root.anchor : null;
    const canNavigate = Boolean((adapter?.navigate || adapter?.highlight) && (root.anchor || root.quote));

    useEffect(() => {
        if (replyOpen) replyRef.current?.focus();
    }, [replyOpen]);

    const go = () => {
        const anchor = root.anchor ?? null;
        const quote = root.quote ?? null;
        let found = false;
        try {
            // highlight = navigate + keep the text marked while this thread is active.
            if (adapter?.highlight) found = adapter.highlight(anchor, quote);
            else if (adapter?.navigate) found = adapter.navigate(anchor, quote);
            else return;
        } catch {
            found = false;
        }
        if (found && adapter?.highlight) onActivate();
        actions.navigated(found);
    };

    // Clicking the card (not one of its controls) jumps to the anchor.
    const onCardClick = (e: ReactMouseEvent<HTMLElement>) => {
        if (!canNavigate) return;
        if ((e.target as HTMLElement).closest("button,a,textarea,input,label,[role=listbox]")) return;
        if (window.getSelection()?.toString()) return; // selecting text to copy
        go();
    };

    const run = async (key: "reply" | "resolve", fn: () => Promise<void>) => {
        setBusy(key);
        setError(null);
        try {
            await fn();
        } catch (err) {
            setError(commentErrorText(err));
        } finally {
            setBusy(null);
        }
    };

    const sendReply = () => {
        const text = draft.trim();
        if (!text || busy) return;
        void run("reply", async () => {
            if (await actions.reply(root.id, text)) {
                setDraft("");
                setReplyOpen(false);
            }
        });
    };

    const closeReply = () => {
        setReplyOpen(false);
        setDraft("");
        window.requestAnimationFrame(() => replyButtonRef.current?.focus());
    };

    const chip = label || root.quote ? (
        <>
            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            <span className="min-w-0 flex-1">
                {label ? <span className="font-medium">{label}</span> : null}
                {root.quote ? <span className="line-clamp-2 break-words italic text-muted-foreground">“{root.quote}”</span> : null}
            </span>
        </>
    ) : null;
    const chipClass =
        "flex w-full items-start gap-1.5 rounded-md border-l-2 border-amber-400 bg-amber-50/70 py-1 pl-2 pr-1.5 text-left text-xs dark:border-amber-500/70 dark:bg-amber-950/25";

    return (
        <article
            aria-label={`Comment by ${root.authorName}${root.isResolved ? ", resolved" : ""}${active ? ", shown in the file" : ""}`}
            aria-current={active || undefined}
            onClick={onCardClick}
            className={cx(
                "flex flex-col gap-2.5 rounded-lg border bg-card p-3 text-card-foreground shadow-sm transition-colors",
                canNavigate && "cursor-pointer",
                active ? "border-amber-500 dark:border-amber-400" : canNavigate && "hover:border-primary/40",
                root.isResolved && "bg-muted/40"
            )}
        >
            {chip ? (
                canNavigate ? (
                    <button
                        type="button"
                        onClick={go}
                        title="Show this in the file"
                        className={cx(chipClass, "hover:bg-amber-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-amber-950/45")}
                    >
                        <span className="sr-only">Show in file: </span>
                        {chip}
                    </button>
                ) : (
                    <div className={chipClass}>{chip}</div>
                )
            ) : null}

            <CommentItem comment={root} people={people} onNeedPeople={onNeedPeople} actions={actions} />

            {replies.length > 0 ? (
                <ol className="space-y-2.5" aria-label={`${replies.length} ${replies.length === 1 ? "reply" : "replies"}`}>
                    {replies.map((r) => (
                        <li key={r.id}>
                            <CommentItem comment={r} people={people} onNeedPeople={onNeedPeople} actions={actions} />
                        </li>
                    ))}
                </ol>
            ) : null}

            {root.isResolved ? (
                <p className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    Resolved{root.resolvedBy ? ` by ${nameFromEmail(root.resolvedBy)}` : ""}
                    {root.resolvedAt ? ` · ${formatCommentTime(root.resolvedAt)}` : ""}
                </p>
            ) : null}
            {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}

            {replyOpen ? (
                <div className="space-y-1.5">
                    <MentionTextarea
                        value={draft}
                        onChange={setDraft}
                        onSubmit={sendReply}
                        onEscape={closeReply}
                        people={people}
                        onNeedPeople={onNeedPeople}
                        label={`Reply to ${root.authorName}`}
                        placeholder="Reply — type @ to mention someone"
                        textareaRef={replyRef}
                        disabled={busy === "reply"}
                    />
                    <div className="flex justify-end gap-1.5">
                        <button type="button" onClick={closeReply} disabled={busy === "reply"} className={SMALL_BTN}>Cancel</button>
                        <button type="button" onClick={sendReply} disabled={!draft.trim() || busy === "reply"} className={PRIMARY_BTN}>
                            {busy === "reply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Send className="h-3.5 w-3.5" aria-hidden />}
                            Reply
                        </button>
                    </div>
                </div>
            ) : canComment && !root.isResolved ? (
                // Mock `.reply`: an input-looking box that opens the reply editor.
                <button
                    ref={replyButtonRef}
                    type="button"
                    onClick={() => setReplyOpen(true)}
                    aria-label={`Reply to ${root.authorName}`}
                    className="block w-full rounded border border-input bg-background px-2 py-1.5 text-left text-[13px] leading-5 text-muted-foreground hover:border-primary/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    Reply…
                </button>
            ) : null}
            {!replyOpen && root.canResolve ? (
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={() => void run("resolve", () => actions.setResolved(root.id, !root.isResolved))}
                        disabled={busy === "resolve"}
                        className={OUTLINE_BTN}
                        title={root.isResolved ? "Reopen this thread" : "Mark as resolved and hide it"}
                    >
                        {busy === "resolve"
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            : root.isResolved ? <RotateCcw className="h-3.5 w-3.5" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />}
                        {root.isResolved ? "Reopen" : "Resolve"}
                    </button>
                </div>
            ) : null}
        </article>
    );
}

function CommentItem({
    comment, people, onNeedPeople, actions
}: {
    comment: DriveComment;
    people: CommentPerson[] | null;
    onNeedPeople: () => void;
    actions: ThreadActions;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(comment.body);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const editRef = useRef<HTMLTextAreaElement>(null);
    const editButtonRef = useRef<HTMLButtonElement>(null);
    const deleteConfirmRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (editing) {
            const el = editRef.current;
            el?.focus();
            el?.setSelectionRange(el.value.length, el.value.length);
        }
    }, [editing]);
    useEffect(() => {
        if (confirmDelete) deleteConfirmRef.current?.focus();
    }, [confirmDelete]);

    if (comment.isDeleted) {
        return <p className="text-xs italic text-muted-foreground">This comment was deleted.</p>;
    }

    const stopEditing = () => {
        setEditing(false);
        setDraft(comment.body);
        setError(null);
        window.requestAnimationFrame(() => editButtonRef.current?.focus());
    };

    const save = async () => {
        const text = draft.trim();
        if (!text || busy) return;
        if (text === comment.body) {
            stopEditing();
            return;
        }
        setBusy(true);
        setError(null);
        try {
            if (await actions.edit(comment.id, text)) setEditing(false);
        } catch (err) {
            setError(commentErrorText(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        setError(null);
        try {
            await actions.remove(comment.id);
        } catch (err) {
            setError(commentErrorText(err));
            setBusy(false);
        }
    };

    // Mock `.c-msg`: avatar | name + time on one line, the text beneath.
    return (
        <div className="grid grid-cols-[26px_minmax(0,1fr)] gap-2">
            <Avatar email={comment.authorEmail} name={comment.authorName} />
            <div className="min-w-0">
                <div className="flex items-start gap-1">
                    <p className="min-w-0 flex-1 truncate text-[13px] leading-5">
                        <span className="font-semibold" title={comment.authorEmail}>{comment.authorName}</span>
                        <time
                            dateTime={comment.createdAt}
                            title={new Date(comment.createdAt).toLocaleString()}
                            className="ml-1.5 text-[11px] text-muted-foreground"
                        >
                            {formatCommentTime(comment.createdAt)}
                        </time>
                        {comment.editedAt ? (
                            <span className="text-[11px] text-muted-foreground" title={`Edited ${new Date(comment.editedAt).toLocaleString()}`}> · edited</span>
                        ) : null}
                    </p>
                    {!editing && !confirmDelete && (comment.canEdit || comment.canDelete) ? (
                        <div className="-mr-1 -mt-1 flex shrink-0">
                            {comment.canEdit ? (
                                <button ref={editButtonRef} type="button" onClick={() => setEditing(true)} aria-label="Edit comment" title="Edit" className={ICON_BTN}>
                                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                                </button>
                            ) : null}
                            {comment.canDelete ? (
                                <button type="button" onClick={() => setConfirmDelete(true)} aria-label="Delete comment" title="Delete" className={ICON_BTN}>
                                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                {editing ? (
                    <div className="mt-2 space-y-1.5">
                        <MentionTextarea
                            value={draft}
                            onChange={setDraft}
                            onSubmit={() => void save()}
                            onEscape={stopEditing}
                            people={people}
                            onNeedPeople={onNeedPeople}
                            label="Edit comment"
                            textareaRef={editRef}
                            disabled={busy}
                        />
                        <div className="flex justify-end gap-1.5">
                            <button type="button" onClick={stopEditing} disabled={busy} className={SMALL_BTN}>Cancel</button>
                            <button type="button" onClick={() => void save()} disabled={!draft.trim() || busy} className={PRIMARY_BTN}>
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                                Save
                            </button>
                        </div>
                    </div>
                ) : (
                    <p className="whitespace-pre-wrap break-words text-[13px] leading-5 text-foreground/90">
                        <MentionText body={comment.body} />
                    </p>
                )}

                {confirmDelete ? (
                    <div role="group" aria-label="Confirm delete" className="mt-2 flex flex-wrap items-center gap-1.5 rounded-md bg-destructive/10 px-2 py-1.5 text-xs">
                        <span className="flex-1">Delete this comment?</span>
                        <button
                            ref={deleteConfirmRef}
                            type="button"
                            onClick={() => void remove()}
                            disabled={busy}
                            className="inline-flex h-7 items-center gap-1 rounded-md bg-destructive px-2.5 text-xs font-medium text-destructive-foreground hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Trash2 className="h-3.5 w-3.5" aria-hidden />}
                            Delete
                        </button>
                        <button type="button" onClick={() => setConfirmDelete(false)} disabled={busy} className={SMALL_BTN}>Cancel</button>
                    </div>
                ) : null}
                {error ? <p role="alert" className="mt-1.5 text-xs text-destructive">{error}</p> : null}
            </div>
        </div>
    );
}
